var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
/**
 * @module cercaliagl/Map
 */
import maplibregl from 'maplibre-gl';
import { transformExtent } from 'ol/proj';
import { register } from 'ol/proj/proj4';
import turfAlong from '@turf/along';
import proj4 from 'proj4';
import { DEFAULT_CENTER, DEFAULT_ZOOM, DEFAULT_MIN_ZOOM, DEFAULT_MAX_ZOOM, THEME, KEY, setAuthkey, } from './globals';
import { i18n, createDictionary } from './dictionary';
import { loadPlugins, checkPluginLoaded, PLUGINS } from './plugins';
import ContextMenu, { Option as ContextMenuOption, OptionId as ContextMenuOptionId, SubmenuRouting as ContextMenuSubmenuRouting, SubmenuRoutingId as ContextMenuSubmenuRoutingId, SubmenuDistance as ContextMenuSubmenuDistance, SubmenuDistanceId as ContextMenuSubmenuDistanceId, } from './ContextMenu';
import LonLat from './LonLat';
import Proj from './proj';
import Bounds from './Bounds';
import { LayersId } from './layer/Base';
import MapTypes from './MapTypes';
import MapEventType from './MapEventType';
import LayerManager from './layer/Manager';
import MarkerLayer from './layer/Marker';
import FeatureLayer from './layer/Feature';
import MapRouting, { MarkerType as MapRoutingMarkerType } from './MapRouting';
import MapDistance from './MapDistance';
import MapStyler from './MapStyler';
import MapControls from './control/MapControls';
import Meteo from './control/Meteo';
import GasStations from './control/GasStations';
import Clean from './control/Clean';
import Isochrones from './control/Isochrones';
import Traffic from './control/Traffic';
import ZBE from './control/ZBE';
import ViewInterface from './control/ViewInterface';
import Geolocation, { EventType as GeolocationEventType } from './Geolocation';
import HttpService from './service/Service';
import Servers from './servers';
import Notification, { Severity } from './Notification';
import LandMarks3DLayer from './layer/LandMarks3D.js';
import { isMobile } from './util';
import Buildings3D from './control/Buildings3D';
import Distance from './service/Distance';
import Draw from './control/Draw';
import LogisticRestrictions from './control/LogisticRestrictions';
/**
 * @typedef {Object} MapOptions
 * @property {string} target HTML element ID map container. Mandatory field.
 * @property {Bounds} [bounds] Initial map bounding box. Default: `null`.
 * @property {LonLat} [center] Center map coordinates. Default values `x=-2.530985346701069`, `y=40.14298996438731`
 * @property {Array.<string>} [controls] Map controls to include. Values:<br/>
 * - `cercaliagl.control.MapControls.SCALELINE`<br/>
 * - `cercaliagl.control.MapControls.CLEAN`<br/>
 * - `cercaliagl.control.MapControls.GAS_STATIONS`<br/>
 * - `cercaliagl.control.MapControls.LAYER_SWITCHER`<br/>
 * - `cercaliagl.control.MapControls.METEO`<br/>
 * - `cercaliagl.control.MapControls.NAVBAR`<br/>
 * - `cercaliagl.control.MapControls.MOBILEBAR`<br/>
 * - `cercaliagl.control.MapControls.TRAFFIC`<br/>
 * - `cercaliagl.control.MapControls.ZOOM`<br/>
 * - `cercaliagl.control.MapControls.LOGISTICS_RESTRICTIONS`<br/>
 * - `cercaliagl.control.MapControls.LAYER_REGIONS`<br/>
 * - `cercaliagl.control.MapControls.ISOCHRONES`<br/>
 * - `cercaliagl.control.MapControls.LOGO`<br/>
 * - `cercaliagl.control.MapControls.BUILDINGS3D`<br/>
 * - `cercaliagl.control.MapControls.DRAW`<br/>
 * - `cercaliagl.control.MapControls.ZBE`<br/>
 * <br/>
 * If you want a map without controls put value `[]`.
 * <br/>
 * @property {string} srcArrow Arrow to be used when feature has showDirections = true<br/>
 * @property {boolean} disableKeyboardControls Disable controls to move/rotate/pan map with the keyboard = true<br/>
 * @property {Array.<import('./MapStyler').StyleLayerJSON>} [drawStyles] Draw styles. Default `[]`.
 * @property {boolean} [adaptDrawStylesToFeatureStyle] Adapt draw styles to feature style. Default `false`.
 * @property {Array.<string>} mapLayers Default map base layers. `cercalia.MapTypes.CERCALIA`, `cercalia.MapTypes.OSM`, `cercalia.MapTypes.SATELLITE``.
 * @property {Array.<import("./layer/XYZ").default|import("./layer/WMS").default>} [customBaseLayers] Custom base layers
 * @property {number} [zoom] Initial zoom level `[0 - 19]`. Default value `6`
 * @property {number} [minZoom] Minimum zoom. Default value `2`
 * @property {number} [maxZoom] Maximum zoom. Default value `19`
 * @property {TollInfoType} [tollInfoType] Format of the toll information displayed when calculating a route. Default value `basic`.
 * @property {string} [tollCurrency] Currency of the toll information displayed when calculating a route. Default value `undefined`.
 * @property {string} [routingNet] Custom routing net. Default value `undefined`.
 * @property {boolean} [cooperativeGestures] Setting cooperativeGesturs to true, scroll to zoom requires command key, and two fingers to pan the map on mobile version.
 * @property {Bounds} [restrictedBounds] Bounding box limits for map navigation. Default `null`.
 * @property {number} [rotation] Map bearing in degrees. Default value `0`
 * @property {Function} [defaultClick] Assign function to click on map.
 * Function parameters:  (1->pixel, 2->{@link cercalia.LonLat}). <br/>
 * 1) Pixel where clicked with mouse right click. <br/>
 * 2) Map coordinate. <br/>
 * <br/>
 * @property {string} [defaultBaseLayer] Default base layer. Default `cercalia`.
 * @property {string} [lang] Language, you must put useBrowserLanguage to false. Default `en`.
 * @property {boolean} [useBrowserLanguage] Enable/Disable the usage by default of the browser language. Default `true`.
 * @property {boolean} [geolocationEnabled] Enable automatically geolocation. Your position will be drawn to map.
 * @property {boolean} [rightClickMenu] Enable/Disable menu on mouse right click. Default `true`.
 * @property {Function} [rightClickFunction] Assign function to mouse right click. For apply this option it's necessary to disable contextmenu on map initialization with option (`rightClickMenu:false`). <br/>
 * The function parameters are:  (1->pixel, 2->{@link cercalia.LonLat}). <br/>
 * 1) Pixel where clicked with mouse right click. <br/>
 * 2) Map coordinate. <br/>
 * <br/>
 * Default: `null`.
 * @property {Array.<string>} [rightClickMenuOptions] Map rightClick menu options to include. Values:<br/>
 * - `cercalia.ContextMenu.Option.ADDRESS`<br/>
 * - `cercalia.ContextMenu.Option.ROUTING`<br/>
 * - `cercalia.ContextMenu.Option.DISTANCE`<br/>
 * <br/>
 * If you want a map without rightClick menu options put value [].
 * <br/>
 * Default `[cercalia.ContextMenuOptions.Location, cercalia.ContextMenuOptions.Routing]`.
 * <br/>
 * @property {boolean} [multipleFeaturesInPixel] Return multiple features in same pixel, for mouse events. Default `true`.
 * @property {number} [pitch] Map pitch. Default value `0`
 * @property {Function} [onRotationEnd] Function is called when the rotation of the map changes (called at the event's end).
 * @property {Function} [onMoveEnd] Function is called when map is moved (called at the event's end).
 * @property {Function} [onZoomEnd] Function is called when the zoom of the map changes (called at the event's end).
 * @property {Function} [onPitchEnd] Function is called when the pitch of the map changes (called at the event's end).
 * @property {boolean} [antialias] Antialias for GL render. Default `false`.
 * @property {string} [themeColor] Theme color
 * @property {routing3d} [routing3d] Draw 3D polyline for calculated route feature. Default `false`.
 * @property {boolean} [freehandOnDrawPolygonMobile] Draw polygon button on mobile will active freehand mode. Default `false`.
 */
/**
 * @typedef {Object} ClusteringOptions
 * @property {string} [layerId] Layer id. Default `LayersId.MARKERS_LAYER_ID`.
 * @property {Function} [onClick] Click over callback function.
 * @property {Function} [onMouseOver] Mouse over callback function.
 * @property {Function} [onMouseOut] Mouse out callback function.
 * @property {number} [maxZoom] Max zoom for clustering. Default `16`.
 * @property {number} [radius] Radius in pixels to cluster. Default `40`.
 */
/**
 * @typedef {Object} HeatmapLayerOptions
 * @property {string} [layerId] Layer id. Default `LayersId.MARKERS_LAYER_ID`.
 * @property {import("./layer/Marker").StyleHeatLayer} styleHeatZones Style layer zone. Read MapboxGL documentation https://docs.mapbox.com/mapbox-gl-js/style-spec/layers/#heatmap.
 * @property {import("./layer/Marker").StyleHeatLayer} styleHeatPoints Point layer zone. Use layer `type: circle` styles. https://docs.mapbox.com/mapbox-gl-js/style-spec/layers/#circle
 */
/**
 * @typedef {Object} DrawOptions
 * @property {Function} [onDrawEnd] Callback function when feature has been drawn. It receive geometry drawn
 */
/**
 * @typedef {Object} CenterOptions
 * @property {boolean} animation Animation.
 * @property {number} duration Duration if animation is enabled. Default `2000`.
 * @property {boolean} changeZoom Change zoom
 * @property {number} zoom zoom to change
 */
/**
 * @type {Array.<string>}
 * @api
 */
export var DefaultMapControls = [
    MapControls.SCALELINE,
    MapControls.CLEAN,
    MapControls.GAS_STATIONS,
    MapControls.LAYER_SWITCHER,
    MapControls.METEO,
    MapControls.NAVBAR,
    MapControls.MOBILEBAR,
    MapControls.TRAFFIC,
    MapControls.ZOOM,
    MapControls.LOGISTICS_RESTRICTIONS,
    MapControls.LAYER_REGIONS,
    MapControls.ISOCHRONES,
    MapControls.LOGO,
    MapControls.BUILDINGS3D,
    MapControls.DRAW,
    MapControls.ZBE,
];
/**
 * @type {CenterOptions}
 */
var defaultCenterOptions = {
    animation: true,
    duration: 2000,
    changeZoom: true,
    zoom: 18,
};
if (!maplibregl.Map.prototype.setCercaliaMap) {
    // eslint-disable-next-line func-names
    maplibregl.Map.prototype.setCercaliaMap = function (map) {
        this.cercaliaMap_ = map;
    };
    // eslint-disable-next-line func-names
    maplibregl.Map.prototype.getCercaliaMap = function () {
        return this.cercaliaMap_;
    };
}
/**
 * @private
 * @return {Promise}
 */
function getAuthkeyAndPlugins() {
    var httpService = new HttpService({ url: Servers.auth });
    var authkeyPromise = httpService.getRequest({
        data: { key: KEY, version: 'gl.1.0b' },
    });
    var scriptsPromises = loadPlugins();
    return Promise.all([authkeyPromise].concat(scriptsPromises));
}
/**
 * @classdesc
 * <b>Javascript ES5</b>
 * ```js
 * const map = new cercaliagl.Map({ ... });
 * ```
 *
 * The map is the main API component. You can use it for painting markers, features, etc... <br/>
 * This class is an implementation of `maplibre.Map` (https://maplibre.org/)<br/>
 * For access to MapboxGL Map object, use the `getMapboxglMap()` method.<br/>
 * To enable 3D landmarks add the parameter `&plugin=threebox` to the URL<br/>
 *
 * This is the main class of Cercali API Javascript. There are many methods such as create/delete markers or features, handle events
 * interaction with map such as zoom in, zoom out, center, etc.
 *
 * It also has lot of properties in constructor. Look carefull at the methods and properties.
 *
 * Example for creating the map:
 *
 * ```js
 * const map = new cercaliagl.Map({
 *   target:'divMap',
 *   controls: []
 * });
 * ```
 *
 * @api
 */
var Map = /** @class */ (function () {
    /**
     * @constructor
     * @param {MapOptions} options Map Options
     */
    function Map(options) {
        var _this = this;
        /**
         * @private
         * @type {string}
         */
        this.containerId_ = options.target;
        /**
         * @private
         * @type {Function}
         */
        this.readyFunction_ = function () { return console.warn('whenReady not called'); }; // eslint-disable-line no-console
        /**
         * @private
         * @type {boolean}
         */
        this.mapReady_ = false;
        /**
         * @private
         * @type {Object.<string, Object>}
         */
        this.iconsLoaded = {};
        /**
         * @private
         * @type {NodeJS.Timeout}
         */
        this.timerLongPress = null;
        /**
         * @private
         * @type {string}
         */
        this.srcArrow_ = options.srcArrow ? options.srcArrow : '';
        /**
         * @private
         * @type {string}
         */
        this.themeColor_ = options.themeColor ? options.themeColor : THEME;
        /**
         * @private
         * @type {number}
         */
        this.minZoom_ = options.minZoom ? options.minZoom : DEFAULT_MIN_ZOOM;
        /**
         * @private
         * @type {boolean}
         */
        this.disableKeyboardControls_ = options.disableKeyboardControls ? options.disableKeyboardControls : false;
        /**
         * @private
         * @type {number}
         */
        this.maxZoom_ = options.maxZoom ? options.maxZoom : DEFAULT_MAX_ZOOM;
        /**
         * @private
         * @type {Function}
         */
        this.onRotationEnd_ = options.onRotationEnd !== undefined ? options.onRotationEnd : null;
        /**
         * @private
         * @type {Function}
         */
        this.onMoveEnd_ = options.onMoveEnd !== undefined ? options.onMoveEnd : null;
        /**
         * @private
         * @type {Function}
         */
        this.onZoomEnd_ = options.onZoomEnd !== undefined ? options.onZoomEnd : null;
        /**
         * @private
         * @type {Function}
         */
        this.onPitchEnd_ = options.onPitchEnd !== undefined ? options.onPitchEnd : null;
        /**
         * @private
         * @type {Array.<string>}
         */
        this.mapLayers_ = options.mapLayers
            ? options.mapLayers
            : [MapTypes.CERCALIA, MapTypes.GOOGLE_STYLE, MapTypes.GOOGLE_STYLE_V2, MapTypes.DARK, MapTypes.SILVER, MapTypes.BLANKMAP, MapTypes.OSM, MapTypes.SATELLITE];
        /**
         * @private
         * @type {import("./layer/XYZ").default|import("./layer/WMS").default}
         */
        this.customBaseLayers_ = options.customBaseLayers ? options.customBaseLayers : [];
        /**
         * @private
         * @type {string}
         */
        this.defaultBaseLayer_ = options.defaultBaseLayer || MapTypes.CERCALIA;
        /**
         * @private
         * @type {number}
         */
        this.currentZoom_ = options.zoom !== undefined ? options.zoom : DEFAULT_ZOOM;
        /**
         * @private
         * @type {number}
         */
        this.currentRotation_ = options.rotation !== undefined ? options.rotation : 0;
        /**
         * @private
         * @type {number}
         */
        this.currentPitch_ = options.pitch !== undefined ? options.pitch : 0;
        /**
         * @private
         * @type {boolean}
         */
        this.threeboxActive_ = false;
        /**
         * @private
         * @type {Array.<import('./MapStyler').StyleLayerJSON>}
         */
        this.drawStyles_ = options.drawStyles ? options.drawStyles : [];
        /**
         * @private
         * @type {boolean}
         */
        this.adaptDrawStylesToFeatureStyle_ = options.adaptDrawStylesToFeatureStyle || false;
        /**
         * @private
         * @type {boolean}
         */
        this.freehandOnDrawPolygonMobile_ = options.freehandOnDrawPolygonMobile ? options.freehandOnDrawPolygonMobile : false;
        getAuthkeyAndPlugins().then(function (data) {
            var authkey = data[0].authkey;
            if (!authkey) {
                var mapContainer = document.querySelector("#".concat(_this.containerId_));
                mapContainer.style.position = 'relative';
                new Notification('Unauthorized domain or key. See console', Severity.ERROR, 60000).display(mapContainer);
                return; // exit
            }
            setAuthkey(authkey);
            /**
             * @private
             * @type {MapStyler}
             */
            _this.mapStyler_ = new MapStyler(_this, _this.customBaseLayers_, _this.defaultBaseLayer_);
            /**
             * @private
             * @type {import("maplibre-gl").Map}
             */
            _this.map_ = new maplibregl.Map({
                container: _this.containerId_,
                style: _this.mapStyler_.getStyles(),
                center: options.center ? options.center.toMapboxLngLat() : DEFAULT_CENTER,
                minZoom: _this.minZoom_,
                maxZoom: _this.maxZoom_,
                zoom: _this.currentZoom_,
                cooperativeGestures: options.cooperativeGestures ? options.cooperativeGestures : false,
                bearing: _this.currentRotation_,
                pitch: _this.currentPitch_,
                maxBounds: options.restrictedBounds ? options.restrictedBounds.toArray() : null,
                preserveDrawingBuffer: true,
                antialias: options.antialias !== undefined ? options.antialias : false,
                keyboard: !_this.disableKeyboardControls_,
            });
            _this.map_.setCercaliaMap(_this);
            if (options.bounds) {
                _this.fitBounds(options.bounds, false);
            }
            /**
             * @private
             * @type {string}
             */
            _this.lang_ = options.lang ? options.lang : undefined; // English value by default
            /**
             * @private
             * @type {boolean}
             */
            _this.useBrowserLanguage = options.useBrowserLanguage !== undefined ? options.useBrowserLanguage : false;
            if (_this.useBrowserLanguage) {
                var navLang = navigator.language || navigator.userLanguage || 'en';
                _this.lang_ = navLang.split('-')[0];
            }
            _this.setLanguage(_this.lang_);
            createDictionary(options.lang || _this.useBrowserLanguage ? _this.lang_ : 'es', true);
            /**
             * @private
             * @type {LayerManagesr}
             */
            _this.layerManager_ = null;
            /**
             * @private
             * @type {ContextMenu}
             */
            _this.contextMenu_ = null;
            /**
             * @private
             * @type {Array.<ContextMenu>}
             */
            _this.contextMenus_ = [];
            /**
             * @private
             * @type {ViewInterface}
             */
            _this.viewInterface_ = null;
            /**
             * @private
             * @type {Function}
             */
            _this.defaultClickMap_ = null;
            if (options.defaultClick) {
                _this.setDefaultClick(options.defaultClick);
            }
            /**
             * @private
             * @type {boolean}
             */
            _this.rightClickMenu_ = options.rightClickMenu !== undefined ? options.rightClickMenu : true;
            if (options.rightClickFunction) {
                _this.initializeCustomContextMenuFunction_(options.rightClickFunction);
            }
            /**
             * @private
             * @type {Array.<string>|null}
             */
            _this.rightClickMenuOptions_ = options.rightClickMenuOptions !== undefined
                ? options.rightClickMenuOptions
                : [ContextMenuOption.ADDRESS, ContextMenuOption.ROUTING, ContextMenuOption.DISTANCE];
            /**
             * @private
             * @type {boolean}
             */
            _this.multipleFeaturesInPixel_ = options.multipleFeaturesInPixel !== undefined ? options.multipleFeaturesInPixel : true;
            /**
             * @private
             * @type {MapRouting}
             */
            _this.mapRouting_ = new MapRouting({
                routing3d: options.routing3d,
                tollInfoType: options.tollInfoType,
                tollCurrency: options.tollCurrency,
                net: options.routingNet,
            });
            /**
             * @private
             * @type {MapDistance}
             */
            _this.mapDistance_ = new MapDistance();
            /**
             * @private
             * @type {HTMLElement}
             */
            _this.canvas_ = _this.map_.getCanvasContainer();
            /**
             * @private
             * @type {Geolocation}
             */
            _this.geolocation_ = options.geolocationEnabled === true ? new Geolocation() : undefined;
            _this.initialize_(options.controls);
        });
    }
    /**
     * @return {import("maplibre-gl").Map} Map object from MapboxGL class
     */
    Map.prototype.getMapboxglMap = function () {
        return this.map_;
    };
    /**
     * @param {Function} callbackFn
     */
    Map.prototype.whenReady = function (callbackFn) {
        this.readyFunction_ = callbackFn;
    };
    /**
     * @api
     */
    Map.prototype.isReady = function () {
        return this.mapReady_;
    };
    /**
     * @private
     * @param {Array.<string>=} [controls] Controls.
     */
    Map.prototype.initialize_ = function (controls) {
        var _this = this;
        this.threeboxActive_ = checkPluginLoaded(PLUGINS.Threebox);
        this.map_.on(MapEventType.READY, function () {
            _this.mapReady_ = true;
            if (_this.readyFunction_) {
                _this.readyFunction_();
            }
        });
        this.map_.once('load', function () {
            _this.initializeMapEvents_();
            _this.initializeMouseEvents_();
            _this.initializeLayers_();
            _this.initializeViewInterface_(controls);
            _this.mapStyler_.initializenSatelliteLayerEvent();
            _this.mapStyler_.onMoveSatelliteLayer();
            _this.map_.fire(MapEventType.READY);
        });
        Map.addProjections();
    };
    /**
     * @private
     * @param {Function} callback function that will be called when element is long-pressed
     */
    Map.prototype.onLongPress_ = function (callback) {
        var _this = this;
        var container = this.map_.getContainer();
        container.addEventListener('touchstart', function (event) {
            var _a;
            if (((_a = event === null || event === void 0 ? void 0 : event.touches) === null || _a === void 0 ? void 0 : _a.length) > 1) {
                _this.clearLongPressTimeout();
            }
            else {
                _this.timerLongPress = setTimeout(function () {
                    _this.timerLongPress = null;
                    callback(event);
                }, 550);
            }
        });
        container.addEventListener('touchend', function (event) {
            if (event.target.tagName === 'CANVAS' && _this.timerLongPress && _this.contextMenu_) {
                _this.contextMenu_.close();
            }
            _this.clearLongPressTimeout();
        });
        container.addEventListener('touchmove', function () { return _this.clearLongPressTimeout(); });
    };
    /**
     * @private
     */
    Map.prototype.clearLongPressTimeout = function () {
        clearTimeout(this.timerLongPress);
        this.timerLongPress = null;
    };
    /**
     * @private
     */
    Map.prototype.initializeMapEvents_ = function () {
        this.map_.on('moveend', this.handleMoveEnd_.bind(this));
        this.map_.on('pitchend', this.handlePitchEnd_.bind(this));
        this.map_.boxZoom.enable();
        if (this.geolocation_) {
            this.initializeGeolocation_();
        }
    };
    /**
     * @private
     */
    Map.prototype.initializeMouseEvents_ = function () {
        this.map_.on('click', this.handleEventClick_.bind(this));
        this.map_.on('contextmenu', this.rightclickEventCallback_.bind(this));
        if (isMobile()) {
            this.initializeMobileEvents_();
        }
    };
    /**
     * @private
     */
    Map.prototype.initializeMobileEvents_ = function () {
        var _this = this;
        this.onLongPress_(function (event) {
            event.preventDefault();
            _this.mobileLongPressEventCallback_(event);
        });
    };
    /**
     * @private
     */
    Map.prototype.initializeLayers_ = function () {
        this.layerManager_ = new LayerManager(this, this.mapStyler_);
        this.featuresLayer_ = new FeatureLayer({ id: LayersId.FEATURE_LAYER_ID, beforeId: 'aerodrome-label', srcArrow: this.srcArrow_ });
        this.markersLayer_ = new MarkerLayer({ id: LayersId.MARKERS_LAYER_ID });
        this.layerManager_.addLayer(this.featuresLayer_);
        this.layerManager_.addLayer(this.markersLayer_);
        if (this.threeboxActive_) {
            this.landMarks3DLayer_ = new LandMarks3DLayer({ visible: false, map: this.map_ });
            this.layerManager_.addLayer(this.landMarks3DLayer_);
        }
    };
    /**
     * @private
     */
    Map.prototype.initializeInteractionsClasses_ = function () {
        this.mapRouting_.setMap(this);
        this.mapDistance_.setMap(this);
    };
    /**
     * @private
     */
    Map.prototype.initializeGeolocation_ = function () {
        var _this = this;
        var mbMarker;
        this.geolocation_.setTracking(true);
        this.geolocation_.on(GeolocationEventType.CHANGE, function () {
            var position = _this.geolocation_.getPosition();
            var lngLat = position.toMapboxLngLat();
            if (!mbMarker) {
                var element = document.createElement('div');
                element.className = 'marker-geolocation';
                element.addEventListener('click', _this.centerOnGeolocation.bind(_this));
                mbMarker = new maplibregl.Marker({ element: element }).setLngLat(lngLat).addTo(_this.map_);
            }
            else {
                mbMarker.setLngLat(lngLat);
            }
        });
    };
    /**
     * Center on current GPS position
     * @api
     */
    Map.prototype.centerOnGeolocation = function () {
        if (this.geolocation_) {
            this.setCenter(this.geolocation_.getPosition(), 17, true);
        }
    };
    /**
     * @private
     * @param {Function} [rightclickFunction] Rightclick function.
     */
    Map.prototype.initializeCustomContextMenuFunction_ = function (rightclickFunction) {
        var _this = this;
        if (rightclickFunction) {
            this.map_.on('contextmenu', function (event) {
                event.preventDefault();
                if (rightclickFunction) {
                    _this.customContextMenuFunctionEvent_(rightclickFunction, event);
                }
            });
        }
    };
    /**
     * @private
     * @param {!Function} rightclickFunction Right click function
     * @param {import('maplibre-gl').MapMouseEvent} event Event
     */
    Map.prototype.customContextMenuFunctionEvent_ = function (rightclickFunction, event) {
        event.preventDefault();
        var pixel = [event.point.x, event.point.y];
        var position = LonLat.fromMapboxgl(event.lngLat);
        rightclickFunction.call(this, pixel, position, event);
    };
    /**
     * @private
     * @param {import('maplibre-gl').MapMouseEvent} event Event
     */
    Map.prototype.handleEventClick_ = function (event) {
        if (document.activeElement.className.indexOf('mdc-text-field__input') !== -1) {
            document.activeElement.blur();
        }
        this.contextMenus_.forEach(function (customContextMenu) {
            customContextMenu.close();
        });
        if (this.viewInterface_) {
            this.viewInterface_.closePopups();
        }
        var position = LonLat.fromMapboxgl(event.lngLat);
        var features = this.map_.queryRenderedFeatures(event.point, { layers: this.layerManager_.getFeaturesLayersIds() });
        // Only one feature if specified in mapOptions
        if (!this.multipleFeaturesInPixel_ && features.length > 0) {
            var featureWithMaxZIndex = features.reduce(function (prev, current) { return (prev.properties.zIndex >= current.properties.zIndex ? prev : current); });
            features = [featureWithMaxZIndex];
        }
        if (features.length === 0) {
            if (this.defaultClickMap_) {
                this.defaultClickMap_(position, event);
            }
        }
    };
    /**
     * Called when 'cercalia-ready' is dispatched.
     * @private
     * @param {Array.<string>} [controls] Controls
     */
    Map.prototype.initializeViewInterface_ = function (controls) {
        var _this = this;
        if (controls === void 0) { controls = DefaultMapControls; }
        // Theme color
        var container = this.map_.getContainer();
        container.style.setProperty('--mdc-theme-primary', this.themeColor_);
        this.viewInterface_ = new ViewInterface({
            target: this.canvas_.parentNode,
            mapLayers: this.mapLayers_,
            drawStyles: this.drawStyles_,
            freehandOnDrawPolygonMobile: this.freehandOnDrawPolygonMobile_,
            adaptDrawStylesToFeatureStyle: this.adaptDrawStylesToFeatureStyle_,
        });
        this.addControl(this.viewInterface_, isMobile() && this.canvas_.parentNode.clientWidth + 30 <= 480 ? 'bottom-right' : 'top-right');
        this.viewInterface_.initializeMapControls(controls, this.defaultBaseLayer_);
        this.updateLogo_();
        this.customBaseLayers_.forEach(function (customBaseLayer) {
            _this.viewInterface_.addCustomLayerToLayerSwitcher(customBaseLayer);
        });
        if (this.rightClickMenuOptions_ && this.rightClickMenuOptions_.length > 0) {
            this.initializeContextMenu_();
        }
        this.initializeInteractionsClasses_();
    };
    /**
     * Creates and returns  the context menu.
     * @private
     */
    Map.prototype.initializeContextMenu_ = function () {
        var _this = this;
        var addressControl = /** @type {import('./control/Address').default} */ (this.viewInterface_.getControlByName(MapControls.ADDRESS));
        this.contextMenu_ = new ContextMenu(
        /** @type {import('./ContextMenu').ContextMenuOptions} */ ({
            map: /** @type {Map} */ (this),
        }));
        if (this.rightClickMenuOptions_.indexOf(ContextMenuOption.ADDRESS) !== -1) {
            this.contextMenu_.addOption(ContextMenuOptionId.ADDRESS, i18n(ContextMenuOption.ADDRESS), function (position) { return addressControl.getDirection(position); });
        }
        if (this.rightClickMenuOptions_.indexOf(ContextMenuOption.ROUTING) !== -1) {
            var subMenuRouting = /** @type {Array.<import('./ContextMenu').ContextMenuSubmenuOption>} */ ([
                {
                    id: ContextMenuSubmenuRoutingId.ROUTING_START,
                    label: i18n(ContextMenuSubmenuRouting.ROUTING_START),
                    callback: this.mapRouting_.setContextMenuRoutingStep.bind(this.mapRouting_, MapRoutingMarkerType.ORIGIN),
                },
                {
                    id: ContextMenuSubmenuRoutingId.ROUTING_STEP,
                    label: i18n(ContextMenuSubmenuRouting.ROUTING_STEP),
                    callback: this.mapRouting_.setContextMenuRoutingStep.bind(this.mapRouting_, MapRoutingMarkerType.STEP),
                },
                {
                    id: ContextMenuSubmenuRoutingId.ROUTING_END,
                    label: i18n(ContextMenuSubmenuRouting.ROUTING_END),
                    callback: this.mapRouting_.setContextMenuRoutingStep.bind(this.mapRouting_, MapRoutingMarkerType.DESTINATION),
                },
            ]);
            this.contextMenu_.addSubMenu(ContextMenuOptionId.ROUTING, i18n(ContextMenuOption.ROUTING), subMenuRouting);
        }
        if (this.rightClickMenuOptions_.indexOf(ContextMenuOption.DISTANCE) !== -1) {
            var subMenuDistance = /** @type {Array.<import('./ContextMenu').ContextMenuSubmenuOption>} */ ([
                {
                    id: ContextMenuSubmenuDistanceId.LINE,
                    label: i18n(ContextMenuSubmenuDistance.LINE),
                    callback: function () { return _this.mapDistance_.enableDistanceInteraction(ContextMenuSubmenuDistanceId.LINE); },
                },
                {
                    id: ContextMenuSubmenuDistanceId.ROAD,
                    label: i18n(ContextMenuSubmenuDistance.ROAD),
                    callback: function () { return _this.mapDistance_.enableDistanceInteraction(ContextMenuSubmenuDistanceId.ROAD); },
                },
                {
                    id: ContextMenuSubmenuDistanceId.AREA,
                    label: i18n(ContextMenuSubmenuDistance.AREA),
                    callback: function () { return _this.mapDistance_.enableDistanceInteraction(ContextMenuSubmenuDistanceId.AREA); },
                },
            ]);
            this.contextMenu_.addSubMenu(ContextMenuOptionId.DISTANCE, i18n(ContextMenuOption.DISTANCE), subMenuDistance);
        }
    };
    /**
     * @param {Object} control Control
     * @param {string} [position] Position
     */
    Map.prototype.addControl = function (control, position) {
        if (control instanceof Meteo
            || control instanceof GasStations
            || control instanceof Clean
            || control instanceof LogisticRestrictions
            || control instanceof Isochrones
            || control instanceof Traffic
            || control instanceof ZBE
            || control instanceof Buildings3D
            || control instanceof Draw
            || control instanceof maplibregl.FullscreenControl) {
            var controlsViewInterface = this.viewInterface_.getControls();
            control.setViewInterface(this.viewInterface_);
            this.map_.addControl(control, position);
            this.viewInterface_.putInContainer(control);
            controlsViewInterface.push(control);
            var dialog = control.getDialog();
            if (dialog) {
                var containerControls = this.map_.getContainer().querySelector('.mapboxgl-control-container');
                if (containerControls) {
                    containerControls.appendChild(dialog);
                }
            }
        }
        else {
            this.map_.addControl(control, position);
        }
    };
    /**
     * Add custom control. You can specify children position inside control container. Default append to last child.
     * @param {import('./control/Custom').default} control Custom control.
     * @param {number} [position] Position.
     * @api
     */
    Map.prototype.addCustomControl = function (control, position) {
        this.viewInterface_.addCustomControl(control, position);
    };
    /**
     * Set default click function. When map is clicked parameter function is triggered.
     * @param {Function|undefined} func Function
     * @api
     */
    Map.prototype.setDefaultClick = function (func) {
        var _this = this;
        this.defaultClickMap_ = function (e) {
            // e.preventDefault();
            if (func && typeof func === 'function') {
                var position = new LonLat(e.getLon(), e.getLat(), Proj.EPSG4326);
                var pixel = _this.coordToPixel(position);
                // const pixel = [e.point.x, e.point.y];
                func(pixel, position);
            }
        };
    };
    /**
     * @private
     * @param {MouseEvent|MapBrowserPointerEvent} event Event.
     */
    Map.prototype.rightclickEventCallback_ = function (event) {
        event.preventDefault();
        if (this.rightClickMenu_ && this.contextMenu_) {
            this.contextMenu_.open(event.lngLat);
            // If map has any elements of MapRouting. Open contextmenu with submenu displayed.
            if (this.mapRouting_.isActive()) {
                this.contextMenu_.openSubmenu(ContextMenuOptionId.ROUTING);
            }
            var routingContextMenu = this.mapRouting_.getContextMenu();
            if (routingContextMenu) {
                routingContextMenu.close();
            }
        }
    };
    /**
     * @private
     * @param {MouseEvent|MapBrowserPointerEvent} event Event.
     */
    Map.prototype.mobileLongPressEventCallback_ = function (event) {
        var _a;
        if (((_a = event.touches) === null || _a === void 0 ? void 0 : _a.length) > 0 && event.target && event.target.tagName === 'CANVAS') {
            var rect = event.target.getBoundingClientRect();
            var pixel = [event.touches[0].clientX - rect.left, event.touches[0].clientY - rect.top];
            var position = this.pixelToCoord(pixel);
            if (this.contextMenu_) {
                this.contextMenu_.open(position);
                // If map has any elements of MapRouting. Open contextmenu with submenu displayed.
                if (this.mapRouting_.isActive()) {
                    this.contextMenu_.openSubmenu(ContextMenuOptionId.ROUTING);
                }
                var routingContextMenu = this.mapRouting_.getContextMenu();
                if (routingContextMenu) {
                    routingContextMenu.close();
                }
            }
        }
    };
    /**
     * @param {ContextMenu} contextMenu Context menu
     * @api
     */
    Map.prototype.setContextMenu = function (contextMenu) {
        this.contextMenu_ = contextMenu;
    };
    /**
     * Get context menu. Useful if user wants to add or remove
     * @return {ContextMenu} Context menu
     * @api
     */
    Map.prototype.getContextMenu = function () {
        return this.contextMenu_;
    };
    /**
     * Open the context menu at the coordinates specified by parameters. If not specified, opens
     * the context menu in the map center according to the current position.
     * @param {LonLat=} [lonLat] Coordinate
     * @api
     */
    Map.prototype.openContextMenu = function (center) {
        if (center === void 0) { center = this.getCenter(); }
        if (this.contextMenu_) {
            this.contextMenu_.open(center);
        }
    };
    /**
     * Add layer
     * @param {import("./layer/Base").LayerLike} layer Layer
     * @param {string} [beforeLayerId] Before layer id. Only when base layer is MVT type.
     * @api
     */
    Map.prototype.addLayer = function (layer, beforeLayerId) {
        this.layerManager_.addLayer(layer);
        if (beforeLayerId) {
            this.layerManager_.moveLayer(layer.getLayersId(), beforeLayerId);
        }
    };
    /**
     * Get layer by id
     * @api
     * @param {string} layerId Layer id
     * @return {import("./layer/Base").LayerLike}
     */
    Map.prototype.getLayerById = function (layerId) {
        return this.layerManager_.getLayerById(layerId);
    };
    /**
     * Remove layer
     * @param {import("./layer/Base").LayerLike} layer Layer
     * @api
     */
    Map.prototype.removeLayer = function (layer) {
        this.layerManager_.removeLayer(layer);
    };
    /**
     * Get map center
     * @return {LonLat} Map center
     * @api
     */
    Map.prototype.getCenter = function () {
        return LonLat.fromMapboxgl(this.map_.getCenter());
    };
    /**
     * @return {Bounds} Current map bounds
     */
    Map.prototype.getBounds = function () {
        var mbBounds = this.map_.getBounds();
        return new Bounds([mbBounds.getWest(), mbBounds.getSouth(), mbBounds.getEast(), mbBounds.getNorth()]);
    };
    /**
     * @return {Boolean} Current map option: multipleFeaturesInPixel
     */
    Map.prototype.getMultipleFeaturesInPixel = function () {
        return this.multipleFeaturesInPixel_;
    };
    /**
     * @param {boolean} [multipleFeaturesInPixel] New value for multipleFeaturesInPixel option.
     */
    Map.prototype.setMultipleFeaturesInPixel = function (multipleFeaturesInPixel) {
        this.multipleFeaturesInPixel_ = multipleFeaturesInPixel;
    };
    /**
     * Return width & height container div in pixels
     * @api
     * @return {Array.<number>} Size in pixels
     */
    Map.prototype.getSize = function () {
        return [this.canvas_.offsetWidth, this.canvas_.offsetHeight];
    };
    /**
   * Returns the container ID
   * @return {string} id Map container Id
   * @api
   */
    Map.prototype.getContainerId = function () {
        return this.containerId_;
    };
    /**
     * Fit the bounds: change the current map bounds to new bounds.
     * @param {Bounds} bounds New bounds
     * @param {boolean} [animation] Enable or disable animation. Default `true`.
     * @param {number} zoom Maximum zoom level when fitting the bounds.
     */
    Map.prototype.fitBounds = function (bounds, animation, zoom) {
        var _a, _b, _c, _d, _e;
        if (animation === void 0) { animation = true; }
        var boundsOptions = bounds.getBoundsOptions();
        var fitOptions = {
            animate: (_a = boundsOptions === null || boundsOptions === void 0 ? void 0 : boundsOptions.animation) !== null && _a !== void 0 ? _a : animation,
            bearing: (_b = boundsOptions === null || boundsOptions === void 0 ? void 0 : boundsOptions.bearing) !== null && _b !== void 0 ? _b : this.currentRotation_,
            duration: (_c = boundsOptions === null || boundsOptions === void 0 ? void 0 : boundsOptions.duration) !== null && _c !== void 0 ? _c : 2000,
            padding: (_d = boundsOptions === null || boundsOptions === void 0 ? void 0 : boundsOptions.padding) !== null && _d !== void 0 ? _d : 50,
            pitch: (_e = boundsOptions === null || boundsOptions === void 0 ? void 0 : boundsOptions.pitch) !== null && _e !== void 0 ? _e : this.currentPitch_,
        };
        if (boundsOptions === null || boundsOptions === void 0 ? void 0 : boundsOptions.maxZoom) {
            fitOptions.maxZoom = boundsOptions.maxZoom;
        }
        else if (zoom) {
            fitOptions.maxZoom = zoom;
        }
        if (this.map_) {
            this.map_.fitBounds(bounds.toDoubleArray(), fitOptions);
        }
    };
    /**
     * Set the map center
     * @param {LonLat} lonLat Coordinates
     * @param {number} [zoom] Zoom
     * @param {boolean} [animation] Enable or disable animation. Default `true`.
     * @param {number} [speed] Speed animation. Default 1.
     * @api
     */
    Map.prototype.setCenter = function (lonLat, zoom, animation, speed) {
        if (animation === void 0) { animation = false; }
        if (speed === void 0) { speed = 1; }
        var mapboxLngLat = lonLat.toMapboxLngLat();
        if (animation) {
            this.map_.flyTo({
                animate: true,
                zoom: zoom,
                center: mapboxLngLat,
                pitch: this.currentPitch_,
                rotation: this.currentRotation_,
                // maxDuration: 5000,
                speed: speed,
                // screenSpeed: speed,
            });
        }
        else {
            this.map_.setCenter(mapboxLngLat);
            if (zoom !== undefined) {
                this.map_.setZoom(zoom);
            }
        }
    };
    /**
     * Moves the map to the new center
     * @param {LonLat} position Coordinates.
     * @param {boolean} [animation] Enable or disable animation. Default `true`.
     * @api
     */
    Map.prototype.panTo = function (position, animation) {
        if (animation === void 0) { animation = true; }
        this.setCenter(position, this.map_.getZoom(), animation);
    };
    /**
     * Map center maintaining the zoom level
     * @param {Bounds} bounds Mapbounds.
     * @param {boolean} [animation] Enable or disable animation. Default `true`.
     * @api
     */
    Map.prototype.panToBounds = function (bounds, animation) {
        var center = bounds.getCenterLonLat();
        if (center !== undefined) {
            this.panTo(center, animation);
        }
    };
    /**
     * @return {number|undefined} Current zoom level of map.
     * @api
     */
    Map.prototype.getZoom = function () {
        return this.map_.getZoom();
    };
    /**
     * Set the zoom level of map. If second parameter is specified (boolean, optional)
     * @param {number} zoom Zoom level to adjust
     * @api
     */
    Map.prototype.setZoom = function (zoom) {
        this.map_.setZoom(zoom);
    };
    /**
     * Returns the pixel value from a coordinate
     * @param {LonLat} lonLat Coordinate.
     * @return {import('./pixel').Pixel} Return pixel of map.
     * @api
     */
    Map.prototype.coordToPixel = function (lonLat) {
        var point = this.map_.project(lonLat.toMapboxLngLat());
        return [point.x, point.y];
    };
    /**
     * Returns the coordinate from a pixel value
     * @param {import('./pixel').Pixel} pixel pixel of map.
     * @return {LonLat} Coordinate
     * @api
     */
    Map.prototype.pixelToCoord = function (pixel) {
        var mbLngLat = this.map_.unproject(pixel);
        return LonLat.fromMapboxgl(mbLngLat);
    };
    /**
     * Calculate the real distance in meters, from pixels values.
     * @param {number} pixels Pixels to measure
     * @return {number|null} meters Return measure in meters.
     * @api
     */
    Map.prototype.pixelsToMeters = function (pixels) {
        var canvas = this.map_.getCanvas();
        var size = [canvas.width, canvas.height];
        if (size !== undefined && size[0] > 0 && size[1] > 0) {
            var extent = this.getBounds().toArray();
            var extentMercator = transformExtent(extent, Proj.EPSG4326, Proj.EPSG3857);
            var widthPixels = size[0];
            var widthMeters = extentMercator[2] - extentMercator[0];
            var meters = (widthMeters / widthPixels) * pixels;
            return meters;
        }
        return null;
    };
    /**
     * @return {LayerManager} Layer manager
     */
    Map.prototype.getLayerManager = function () {
        return this.layerManager_;
    };
    /**
     * @return {MapStyler} Map styler
     */
    Map.prototype.getMapStyler = function () {
        return this.mapStyler_;
    };
    /**
     * @param {import("./Marker").default} marker Marker
     * @param {MarkerLayer} [layer]
     * @api
     */
    Map.prototype.addMarker = function (marker, layer) {
        if (layer === void 0) { layer = this.markersLayer_; }
        layer.addMarker(marker);
        marker.setMap(this);
    };
    /**
     * @param {Array.<import("./Marker").default>} marker Marker
     * @param {MarkerLayer} [layer] Layer
     * @api
     */
    Map.prototype.addMarkers = function (markers, layer) {
        var _this = this;
        if (layer === void 0) { layer = this.markersLayer_; }
        // layer.addMarkers(markers);
        markers.forEach(function (marker) { return _this.addMarker(marker, layer); });
    };
    /**
     * @param {import("./Marker").default} marker Marker
     * @param {MarkerLayer} [layer] Layer
     * @api
     */
    Map.prototype.removeMarker = function (marker, layer) {
        if (layer === void 0) { layer = this.markersLayer_; }
        layer.removeMarker(marker);
    };
    /**
     * @param {Array.<import("./Marker").default>} markers
     * @param {MarkerLayer} [layer]
     * @api
     */
    Map.prototype.removeMarkers = function (markers, layer) {
        if (layer === void 0) { layer = this.markersLayer_; }
        layer.removeMarkers(markers);
    };
    /**
     * @param {MarkerLayer} [layer]
     * @api
     */
    Map.prototype.removeAllMarkers = function (layer) {
        if (layer === void 0) { layer = this.markersLayer_; }
        layer.removeAllMarkers();
    };
    /**
     * @param {import("./Feature").default} feature Feature
     * @param {FeatureLayer} [layer] Feature layer
     * @param {boolean} [updateGeoJSON] Update GeoJSON after add feature, true by default
     * @api
     */
    Map.prototype.addFeature = function (feature, layer, updateGeoJSON) {
        if (layer === void 0) { layer = this.featuresLayer_; }
        if (updateGeoJSON === void 0) { updateGeoJSON = true; }
        layer.addFeature(feature, updateGeoJSON);
        feature.setMap(this);
    };
    /**
     * @param {import("./Feature").default} feature Feature
     * @param {FeatureLayer} [layer] Feature layer
     * @param {Function} [callbackFn] callback function when finished
     * @api
     */
    Map.prototype.addAnimatedFeature = function (feature, layer, callbackFn) {
        if (layer === void 0) { layer = this.featuresLayer_; }
        layer.addAnimatedFeature(feature, callbackFn);
        feature.setMap(this);
    };
    /**
     * @param {Array.<import("./Feature").default>} features Features
     * @param {FeatureLayer} [layer] Feature layer
     * @api
     */
    Map.prototype.addFeatures = function (features, layer) {
        if (layer === void 0) { layer = this.featuresLayer_; }
        layer.addFeatures(features, this);
    };
    /**
     * @param {import("./Feature").default} feature Feature
     * @param {FeatureLayer} [layer] Feature layer
     * @api
     */
    Map.prototype.removeFeature = function (feature, layer) {
        if (layer === void 0) { layer = this.featuresLayer_; }
        layer.removeFeature(feature);
    };
    /**
     * @param {import("./Feature").default} feature Feature
     * @param {FeatureLayer} [layer] Feature layer
     * @api
     */
    Map.prototype.removeFeatures = function (features, layer) {
        if (layer === void 0) { layer = this.featuresLayer_; }
        layer.removeFeatures(features);
    };
    /**
     * @param {FeatureLayer} [layer] Feature layer
     * @api
     */
    Map.prototype.removeAllFeatures = function (layer) {
        if (layer === void 0) { layer = this.featuresLayer_; }
        layer.removeAllFeatures();
    };
    /**
     * @private
     */
    Map.prototype.handleMoveEnd_ = function () {
        var old; // on zoom end i on rotation end
        if (this.map_.getBearing() !== this.currentRotation_) {
            old = this.currentRotation_;
            this.currentRotation_ = this.map_.getBearing();
            if (this.onRotationEnd_) {
                this.onRotationEnd_({ new: this.currentRotation_, old: old });
            }
        }
        if (this.map_.getZoom() !== this.currentZoom_) {
            old = this.currentZoom_;
            this.currentZoom_ = this.map_.getZoom();
            if (this.layerManager_.isBuildings3DEnabled()) {
                var MIN_ZOOM = 15;
                if (this.currentZoom_ >= MIN_ZOOM && old < MIN_ZOOM) {
                    this.map_.flyTo({ pitch: 60, duration: 1000 });
                }
                else if (this.currentZoom_ < MIN_ZOOM && old >= MIN_ZOOM) {
                    this.map_.flyTo({ pitch: 0, duration: 1000 });
                }
            }
            if (this.onZoomEnd_) {
                this.onZoomEnd_({ new: this.currentZoom_, old: old });
            }
        }
        if (this.onMoveEnd_) {
            this.onMoveEnd_({
                center: this.getCenter(),
                zoom: this.getZoom(),
            });
        }
    };
    /**
     * @private
     */
    Map.prototype.handlePitchEnd_ = function () {
        if (this.onPitchEnd_ !== null && this.map_.getPitch() !== this.currentPitch_) {
            var old = this.currentPitch_;
            this.currentPitch_ = this.map_.getPitch();
            this.onPitchEnd_({ new: this.currentPitch_, old: old });
        }
    };
    /**
     * @api
     * @param {MarkerLayer} [layer]
     */
    Map.prototype.getMarkers = function (layer) {
        if (layer === void 0) { layer = this.markersLayer_; }
        return layer ? layer.getMarkers() : null;
    };
    /**
     * Get marker by ID. It returns null if it is not found.
     * @param {string} id Id.
     * @param {MarkerLayer} [layer] Marker layer.
     * @return {import("./Marker").default|null} marker Marker or `null` if not found.
     * @api
     */
    Map.prototype.getMarkerById = function (id, layer) {
        if (layer === void 0) { layer = this.markersLayer_; }
        return layer.getMarkerById(id);
    };
    /**
     * @api
     * @param {FeatureLayer} [layer]
     */
    Map.prototype.getFeatures = function (layer) {
        if (layer === void 0) { layer = this.featuresLayer_; }
        return layer ? layer.getFeatures() : null;
    };
    /**
     * Get marker by ID. It returns null if it is not found.
     * @param {string} id Id.
     * @param {FeatureLayer} [layer] Marker layer.
     * @return {import("./Feature").default|null} marker Marker or `null` if not found.
     * @api
     */
    Map.prototype.getFeatureById = function (id, layer) {
        if (layer === void 0) { layer = this.featuresLayer_; }
        return layer.getFeatureById(id);
    };
    /**
     * Close all open popups
     * @param {MarkerLayer|undefined} [layer]
     * @api
     */
    Map.prototype.closePopups = function (layer) {
        if (layer === void 0) { layer = undefined; }
        var markers = this.getMarkers(layer);
        for (var i = 0; i < markers.length; i += 1) {
            var popup = markers[i].getPopup();
            if (popup) {
                popup.getMapboxPopup().remove();
            }
        }
    };
    /**
     * hide all marker labels
     * @param {MarkerLayer|undefined} [layer]
     * @api
     */
    Map.prototype.hideLabels = function (layer) {
        if (layer === void 0) { layer = undefined; }
        var markers = this.getMarkers(layer);
        for (var i = 0; i < markers.length; i += 1) {
            markers[i].hideLabel();
        }
    };
    /**
     * show all marker labels
     * @param {MarkerLayer|undefined} [layer]
     * @api
     */
    Map.prototype.showLabels = function (layer) {
        if (layer === void 0) { layer = undefined; }
        var markers = this.getMarkers(layer);
        for (var i = 0; i < markers.length; i += 1) {
            markers[i].showLabel();
        }
    };
    /**
     * Show all markers popups. Only for markers with popup created
     * @api
     */
    Map.prototype.openPopups = function () {
        var markers = this.getMarkers();
        for (var i = 0; i < markers.length; i += 1) {
            var popup = markers[i].getPopup();
            if (popup) {
                popup.show();
            }
        }
    };
    /**
     * @param {Array.<import("./Marker").default>} [markers] Markers
     * @param {CenterOptions} options Options
     * @api
     */
    Map.prototype.centerToMarkers = function (markers, options) {
        if (options === void 0) { options = defaultCenterOptions; }
        var bounds = new Bounds();
        if (markers) {
            markers.forEach(function (marker) { return bounds.extend(marker.getPosition()); });
        }
        else {
            var allMarkers = this.markersLayer_.getMarkers();
            allMarkers.forEach(function (marker) { return bounds.extend(marker.getPosition()); });
        }
        this.centerElementsInner_(bounds, options);
    };
    /**
     * @param {Array.<import("./Feature").default>} [features] Features
     * @param {CenterOptions} options Options
     * @api
     */
    Map.prototype.centerToFeatures = function (features, options) {
        if (options === void 0) { options = defaultCenterOptions; }
        var bounds = new Bounds();
        if (features) {
            features.forEach(function (feature) { return bounds.extend(feature.getBounds()); });
        }
        else {
            var layer = this.layers_[LayersId.FEATURE_LAYER_ID];
            var allFeatures = layer.getFeatures();
            allFeatures.forEach(function (feature) { return bounds.extend(feature.getBounds()); });
        }
        this.centerElementsInner_(bounds, options);
    };
    /**
     * @private
     * @param {Bounds} bounds bounds
     * @param {boolean} changeZoom Change zoom. Default `true`.
     * @param {boolean} animation Enable or disable animation. Default `true`
     */
    Map.prototype.centerElementsInner_ = function (bounds, options) {
        var _a;
        if (bounds.getTop()) {
            if (options.changeZoom) {
                if (bounds.getWidthInMeters() > 200 || bounds.getHeightInMeters() > 120) {
                    this.fitBounds(bounds, options.animation, options.zoom);
                }
                else {
                    var center = bounds.getCenterLonLat();
                    if (center !== undefined) {
                        this.setCenter(center, (_a = options.zoom) !== null && _a !== void 0 ? _a : defaultCenterOptions.zoom, options.animation);
                    }
                }
            }
            else {
                var center = bounds.getCenterLonLat();
                if (center !== undefined) {
                    this.panTo(center, options.animation);
                }
            }
        }
    };
    /**
     * Sets the map rotation
     * @param {number} bearing Angle in degrees.
     * @param {boolean} [animation] Animation. Default `true`.
     * @api
     */
    Map.prototype.setRotation = function (bearing, animation) {
        if (animation === void 0) { animation = true; }
        if (animation) {
            this.map_.flyTo({ bearing: bearing });
        }
        else {
            this.map_.setBearing(bearing);
        }
    };
    /**
     * @param {number} pitch Pitch
     * @param {boolean} [animation] Animation. Default `true`.
     * @api
     */
    Map.prototype.setPitch = function (pitch, animation) {
        if (animation === void 0) { animation = true; }
        this.pitch_ = pitch;
        if (animation) {
            this.map_.flyTo({ pitch: pitch });
        }
        else {
            this.map_.setPitch(this.pitch_);
        }
    };
    /**
     * @return {number} Rotation in degrees.
     * @api
     */
    Map.prototype.getRotation = function () {
        return this.map_.getBearing();
    };
    /**
     * Rotates the map <angle> degrees, by parameter
     * @param {number} angle Angle (degrees).
     * @param {boolean} [animation] Animation. Default `true`.
     * @api
     */
    Map.prototype.rotate = function (angle, animation) {
        if (animation === void 0) { animation = true; }
        var bearing = this.map_.getBearing();
        if (animation) {
            this.map_.flyTo({ bearing: bearing + angle });
        }
        else {
            this.map_.setBearing(bearing + angle);
        }
    };
    /**
     * Export PNG image
     * @param {string} [filename] Filename.
     * @api
     */
    Map.prototype.exportPNG = function (filename) {
        if (filename === void 0) { filename = 'ExportCercaliaMap.png'; }
        var exportPNGElement = document.createElement('a');
        var canvas = this.map_.getCanvas();
        exportPNGElement.addEventListener('click', function () {
            exportPNGElement.download = filename;
            exportPNGElement.href = canvas.toDataURL('image/png');
        }, false);
        document.body.appendChild(exportPNGElement);
        exportPNGElement.click();
        document.body.removeChild(exportPNGElement);
    };
    /**
     * @param {import("./InfoMap").default} infoMap Info map.
     * @api
     */
    Map.prototype.showInfoMap = function (infoMap) {
        this.viewInterface_.closePopups();
        var infoMapControl = this.viewInterface_.getInfoMapControl();
        infoMapControl.update(infoMap);
        infoMapControl.showPopup();
    };
    /**
     * Close all menus and reset nav bar menu view.
     * @api
     */
    Map.prototype.closeAll = function () {
        if (this.viewInterface_) {
            this.viewInterface_.closeAll();
        }
        if (this.contextMenu_) {
            this.contextMenu_.close();
        }
    };
    /**
     * Close context menu
     */
    Map.prototype.closeContextMenus = function () {
        this.contextMenus_.forEach(function (contextMenu) {
            contextMenu.close();
        });
    };
    /**
     * Add custom context menu. Method not published thorught API
     * @param {cercalia.ContextMenu} contextMenu Context menu
     */
    Map.prototype.addContextMenu = function (contextMenu) {
        this.contextMenus_.push(contextMenu);
    };
    /**
     * @api
     * @return {MapRouting} Map routing object
     */
    Map.prototype.getMapRouting = function () {
        return this.mapRouting_;
    };
    /**
     * @api
     * @return {MapDistance} Map routing object
     */
    Map.prototype.getMapDistance = function () {
        return this.mapDistance_;
    };
    /**
     * @api
     * @return {Geolocation} Geolocation
     */
    Map.prototype.getGeolocation = function () {
        return this.geolocation_;
    };
    /**
     * @api
     * @param {import("./layer/XYZ").default|import("./layer/WMS").default} baseLayer Base layer
     * @param {string | undefined} mapType Map type
     */
    Map.prototype.addBaseLayer = function (baseLayer, mapType) {
        if (mapType === void 0) { mapType = undefined; }
        this.mapStyler_.addBaseLayer(baseLayer, mapType);
        this.viewInterface_.addCustomLayerToLayerSwitcher(baseLayer);
    };
    /**
     * @api
    *  @param {string} id id
     * @param {string} label label
     * @param {Array<import("./layer/XYZ").default|import("./layer/WMS").default>} baseLayers Base layers
     * @param {string | undefined} mapType Map type
     */
    Map.prototype.addGroupBaseLayer = function (id, label, baseLayers, mapType) {
        if (mapType === void 0) { mapType = undefined; }
        var finalJson = {
            version: 8,
            attribution: this.attribution_,
            glyphs: Servers.glyphs,
            sources: {},
            layers: [
                {
                    id: 'buildings3d',
                    type: 'fill-extrusion',
                    source: 'buildings3d',
                    'source-layer': 'building',
                    minzoom: 15,
                    layout: { visibility: 'none' },
                    paint: {
                        'fill-extrusion-color': 'rgba(187, 187, 187, 1)',
                        'fill-extrusion-opacity': 0.6,
                        'fill-extrusion-height': ['get', 'height'],
                        'fill-extrusion-base': ['get', 'min_height'],
                    },
                },
                {
                    id: '3dlandmarks',
                    type: 'fill',
                    source: '3dlandmarks',
                    'source-layer': '3dlandmarks',
                    minzoom: 15,
                    paint: {
                        'fill-color': '#088',
                        'fill-opacity': 0,
                    },
                    layout: {
                        visibility: 'none',
                    },
                },
            ],
        };
        baseLayers.forEach(function (baseLayer) {
            var layerStyle = baseLayer.getMapboxStyle();
            Object.keys(layerStyle.sources).forEach(function (key) { finalJson.sources[key] = layerStyle.sources[key]; });
            finalJson.layers = finalJson.layers.concat(layerStyle.layers.filter(function (layer) { return layer.id !== 'buildings3d' && layer.id !== '3dlandmarks'; }));
        });
        this.mapStyler_.addBaseLayerFromJson(id, finalJson, mapType);
        this.viewInterface_.addCustomLayerToLayerSwitcherFromJson(id, label);
    };
    /**
     * @param {string} id id
     * @param {string} label label
     * @param {maplibregl.Style} baseLayer Base layer
     * @param {string | undefined} mapType Map type
     */
    Map.prototype.addBaseLayerFromJson = function (id, label, baseLayer, mapType) {
        if (mapType === void 0) { mapType = undefined; }
        this.mapStyler_.addBaseLayerFromJson(id, baseLayer);
        this.viewInterface_.addCustomLayerToLayerSwitcherFromJson(id, label, mapType);
    };
    /**
     * @api
     * @param {string} name Name
     */
    Map.prototype.getControlByName = function (name) {
        return this.viewInterface_.getControlByName(name);
    };
    /**
     * Available layers. <br/>
     * - `cercaliagl.MapTypes.CERCALIA`<br/>
     * - `cercaliagl.MapTypes.SATELLITE`<br/>
     * - `cercaliagl.MapTypes.OSM`<br/>
     * - `cercaliagl.MapTypes.DARK`<br/>
     * - `cercaliagl.MapTypes.SILVER`<br/>
     * - `cercaliagl.MapTypes.BLANKMAP`<br/>
     * - `cercaliagl.MapTypes.GOOGLE_STYLE`<br/>
     * - `cercaliagl.MapTypes.EMPTY`<br/>
     * @api
     * @param {string} baseLayer Base layer
     */
    Map.prototype.changeBaseLayer = function (baseLayer) {
        var _this = this;
        this.mapStyler_.setBaseLayer(baseLayer);
        this.map_.once('style.load', function () {
            _this.mapStyler_.onMoveSatelliteLayer();
            _this.showBuildings3D(_this.layerManager_.isBuildings3DEnabled());
        });
        this.updateLogo_();
    };
    /**
     * @private
     */
    Map.prototype.updateLogo_ = function () {
        var styles = this.mapStyler_.getStyles();
        var logoCtrl = this.viewInterface_.getLogo();
        if (logoCtrl) {
            logoCtrl.setAttribution(styles.attribution || '');
        }
    };
    /**
     * @api
     * @param {string} eventName Event name
     * @param {Function} callbackFn Callback function
     */
    Map.prototype.on = function (eventName, callbackFn) {
        this.map_.on(eventName, callbackFn);
    };
    /**
     * @api
     * @param {string} eventName Event name
     * @param {Function} callbackFn Callback function
     */
    Map.prototype.off = function (eventName, callbackFn) {
        this.map_.off(eventName, callbackFn);
    };
    /**
     * Enable draw interaction
     * @api
     * @param {string} type Available values `line|polygon|draw_magnetic_polygon|circle|freehand_polygon`
     * @param {DrawOptions} options Draw options
     */
    Map.prototype.enableDrawInteraction = function (type, options) {
        var drawControl = /** @type {import("./control/Draw").default} */ (this.getControlByName(MapControls.DRAW));
        if (type === 'line') {
            drawControl.setDrawLinestring();
        }
        else if (type === 'polygon') {
            drawControl.setDrawPolygonMode();
        }
        else if (type === 'draw_magnetic_polygon') {
            drawControl.setDrawMagneticPolygonMode();
        }
        else if (type === 'freehand_polygon') {
            drawControl.setDrawFreehandPolygonMode();
        }
        else if (type === 'circle') {
            drawControl.setDrawCircleMode();
        }
        else {
            console.warn("Unknown type: ".concat(type)); // eslint-disable-line no-console
            return;
        }
        this.map_.once('draw.create', function (e) {
            if ((!e.features[0] || !options || !options.onDrawEnd)) {
                setTimeout(function () { return drawControl.setStaticMode(); }, 50);
                return;
            }
            if (((type === 'polygon' || type === 'draw_magnetic_polygon' || type === 'freehand_polygon') && !drawControl.checkIfPolygonDrawedOrModifiedCanNotBeDrawn_(e))) {
                options.onDrawEnd(e.features[0].geometry, __assign(__assign({}, e.features[0].properties), { id: e.features[0].id }));
            }
            else if (e.features[0] && (type === 'line' || type === 'circle')) {
                options.onDrawEnd(e.features[0].geometry, __assign(__assign({}, e.features[0].properties), { id: e.features[0].id }));
            }
            setTimeout(function () { return drawControl.setStaticMode(); }, 50);
        });
    };
    /**
     * Disable draw interaction
     * @api
     */
    Map.prototype.disableDrawInteraction = function () {
        var drawControl = /** @type {import("./control/Draw").default} */ (this.getControlByName(MapControls.DRAW));
        if (drawControl) {
            var currentMode = drawControl.getCurrentMode();
            drawControl.setStaticMode();
            if (currentMode !== 'simple_select' && currentMode !== 'static') {
                var features = drawControl.getGeoJSONFeatures();
                var lastDrawnFeature = features.reverse().find(function (feature) { var _a; return ((_a = feature.properties) === null || _a === void 0 ? void 0 : _a.snapGuide) !== 'true'; }); // Not boolean, it comes like string
                if (lastDrawnFeature) {
                    drawControl.deleteFeature(lastDrawnFeature.id);
                }
            }
        }
    };
    /**
     * Enable marker clustering. Only available for {@link module:cercaliagl/layer/Marker~Marker} layer.
     * @param {ClusteringOptions} options Clustering options
     * @api
     */
    Map.prototype.enableClustering = function (options) {
        if (options === void 0) { options = {}; }
        var layerId = options.layerId ? options.layerId : LayersId.MARKERS_LAYER_ID;
        var layer = /** @type {MarkerLayer} */ (this.layerManager_.getLayerById(layerId));
        if (layer instanceof MarkerLayer) {
            layer.onClusterClick = options.onClick;
            layer.onClusterMouseOut = options.onMouseOut;
            layer.onClusterMouseOver = options.onMouseOver;
            layer.enableClustering(options.maxZoom, options.radius);
        }
        else {
            console.error('Function only available for MarkerLayers'); // eslint-disable-line no-console
        }
    };
    /**
     * Disable marker clustering. Only available for  {@link module:cercaliagl/layer/Marker~Marker} layer.
     * @param {string} [layerId] Layer id. Default `LayersId.MARKERS_LAYER_ID`.
     * @api
     */
    Map.prototype.disableClustering = function (layerId) {
        if (layerId === void 0) { layerId = LayersId.MARKERS_LAYER_ID; }
        var layer = /** @type {MarkerLayer} */ (this.layerManager_.getLayerById(layerId));
        if (layer instanceof MarkerLayer) {
            layer.onClusterClick = null;
            layer.onClusterMouseOut = null;
            layer.onClusterMouseOver = null;
            layer.disableClustering();
        }
        else {
            console.error('Function only available for MarkerLayers'); // eslint-disable-line no-console
        }
    };
    /**
     * Converts layer into heatmap. There are two layers: Zone layer for heat, and points layer. Points layers is useful to show markers in the nearest zooms.
     * For better adaptation customize layer styles. Depends of number and density you should adjust heat layer styles.
     * Read MapboxGL documentation to styling both layers: https://docs.mapbox.com/mapbox-gl-js/style-spec/layers/#heatmap.
     *
     * Default values for styles for zones if `options.styleHeatZones` property is not provided:
     *
     * ```json
     * {
     *   maxzoom: 15,
     *   paint: {
     *     'heatmap-intensity': [
     *       'interpolate', ['linear'], ['zoom'], 0, 0.1, 15, 1.5,
     *     ],
     *     'heatmap-color': [
     *       'interpolate', ['linear'],
     *       ['heatmap-density'], 0, 'rgba(0, 0, 255, 0)', 0.1, 'royalblue', 0.3, 'cyan', 0.5, 'lime', 0.7, 'yellow', 1, 'red',
     *     ],
     *     'heatmap-radius': [
     *       'interpolate', ['linear'], ['zoom'], 0, 1, 15, 14,
     *     ],
     *     'heatmap-opacity': [
     *       'interpolate', ['linear'], ['zoom'], 7, 0.8, 15, 0.3,
     *     ],
     *   },
     * }
     * ```
     *
     * Default values for styles for points if `options.styleHeatPoints` property is not provided:
     *
     * ```json
     * {
     *   minzoom: 14,
     *   paint: {
     *     'circle-radius': [
     *       'interpolate', ['linear'], ['zoom'], 12, 3, 16, 6,
     *     ],
     *     'circle-color': 'rgba(0,0,0,0.4)',
     *     'circle-stroke-color': 'white',
     *     'circle-stroke-width': 1,
     *   },
     * }
     * ```
     *
     * @param {HeatmapLayerOptions} options Heatmap layer options
     * @api
     */
    Map.prototype.enableHeatmap = function (options) {
        if (options === void 0) { options = {}; }
        var layerId = options.layerId ? options.layerId : LayersId.MARKERS_LAYER_ID;
        var layer = /** @type {MarkerLayer} */ (this.layerManager_.getLayerById(layerId));
        layer.enableHeatmap(options.styleHeatPoints, options.styleHeatZones);
    };
    /**
     * @param {string} [layerId] Layer id. Default `LayersId.MARKERS_LAYER_ID`.
     * @api
     */
    Map.prototype.disableHeatmap = function (layerId) {
        if (layerId === void 0) { layerId = LayersId.MARKERS_LAYER_ID; }
        var layer = /** @type {MarkerLayer} */ (this.layerManager_.getLayerById(layerId));
        layer.disableHeatmap();
    };
    /**
     * Show 3D buildings in close zooms.
     * @param {boolean} visible Visible or not.
     * @param {boolean} [showToBuildings] Buildings
     * @api
     */
    Map.prototype.showBuildings3D = function (visible, showToBuildings) {
        if (showToBuildings === void 0) { showToBuildings = false; }
        this.layerManager_.enableBuildings3D(visible);
        if (this.threeboxActive_ && this.map_.getLayer(LayersId.LANDMARKS3D)) {
            this.landMarks3DLayer_.setVisible(visible);
        }
        var building3dControl = this.getControlByName(MapControls.BUILDINGS3D);
        if (building3dControl) {
            if (visible) {
                building3dControl.enable3dBuildingInterface();
            }
            else {
                building3dControl.disable3dBuildingInterface();
            }
        }
        if (showToBuildings) {
            var MIN_ZOOM = 15;
            if (this.currentZoom_ <= MIN_ZOOM) {
                this.map_.flyTo({ pitch: 60, duration: 1000, zoom: MIN_ZOOM });
            }
        }
    };
    /**
     * @param {string} lang Language
     * @api
     */
    Map.prototype.setLanguage = function (lang) {
        this.lang_ = lang;
        this.mapStyler_.setLanguage(lang);
        if (this.mapRouting_) {
            this.mapRouting_.setLang(lang);
        }
    };
    /**
     * @return {string}  Language
     * @api
     */
    Map.prototype.getLang = function () {
        return this.lang_;
    };
    /**
     * Toggle full screen, only available inside any button event.
     * @api
     */
    Map.prototype.toggleFullScreen = function () {
        var fullScreenControl = this.getControlByName(MapControls.FULLSCREEN);
        fullScreenControl.toggle();
    };
    /**
     * Force a recalculation of the map viewport size. This should be called when map container changes its size
     * @api
     */
    Map.prototype.updateSize = function () {
        this.map_.resize();
    };
    /**
     * Hide all map controls
     * @api
    */
    Map.prototype.hideControls = function () {
        this.viewInterface_.hideAll();
    };
    /**
     * Show map controls
     * @api
    */
    Map.prototype.showControls = function () {
        this.viewInterface_.showAll();
    };
    /**
     * @api
     * @param {Marker} marker Marker that will be animated
     * @param {Feature} feature Feature (type linestring) where marker will be animated
     * @param {number} speed Speed of marker
     * @param {number} numSteps Number of steps to divide the marker path
     */
    Map.animateMarker = function (marker, feature, speed, numSteps) {
        var speedMs = speed * (1000 / 3600); // in m/s
        var type = feature.getGeometry().type;
        if (type === 'LineString') {
            var distance = Distance.calculateDistanceOnLine(feature.getGeoJSON(), 'kilometers') * 1000;
            var timeMs = (distance / speedMs) * 1000; // Time travel in ms
            var intervalTime = timeMs / numSteps;
            var coords_1 = [];
            var arc = [];
            var route = feature.getGeoJSON();
            for (var i = 0; i < distance; i += distance / numSteps) {
                var segment = turfAlong(route, i, { units: 'meters' });
                arc.push(segment.geometry.coordinates);
            }
            coords_1.push.apply(coords_1, arc);
            var pos_1 = 0;
            var intervalId_1 = setInterval(function () {
                var coordAct = coords_1[pos_1];
                pos_1 += 1;
                if (coordAct) {
                    marker.setPosition(new LonLat(coordAct[0], coordAct[1]));
                }
                else {
                    clearInterval(intervalId_1);
                    intervalId_1 = undefined;
                }
            }, intervalTime);
        }
        else {
            // eslint-disable-next-line no-console
            console.error('Feature is not a LineString');
        }
    };
    /**
     * Add more projections to be able to convert WKTs to that encodings in Open Layers
     * Get encodings from https://epsg.io
     * @private
     */
    Map.addProjections = function () {
        proj4.defs('EPSG:3395', '+proj=merc +lon_0=0 +k=1 +x_0=0 +y_0=0 +datum=WGS84 +units=m +no_defs +type=crs');
        register(proj4);
    };
    return Map;
}());
export default Map;
//# sourceMappingURL=Map.js.map